import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Cinematic loading screen shown on first visit (3.5s auto-dismiss).
 * - Greek "ΚΑΘΙΕΡΩΜΕΝΗ ΑΡΙΣΤΕΙΑ" is the headline.
 * - The same phrase scrolls/fades through 7 other languages below as a marquee strip.
 * - Click anywhere to skip immediately.
 * - Once dismissed, sessionStorage prevents it from showing again in the same tab.
 */

const TAGLINES = [
  { code: 'el', label: 'ΚΑΘΙΕΡΩΜΕΝΗ ΑΡΙΣΤΕΙΑ', rtl: false },
  { code: 'en', label: 'ESTABLISHED EXCELLENCE', rtl: false },
  { code: 'de', label: 'ETABLIERTE EXZELLENZ', rtl: false },
  { code: 'ru', label: 'ПРИЗНАННОЕ СОВЕРШЕНСТВО', rtl: false },
  { code: 'ro', label: 'EXCELENȚĂ CONSACRATĂ', rtl: false },
  { code: 'fr', label: 'EXCELLENCE ÉTABLIE', rtl: false },
  { code: 'es', label: 'EXCELENCIA ESTABLECIDA', rtl: false },
  { code: 'pt', label: 'EXCELÊNCIA ESTABELECIDA', rtl: false },
  { code: 'pl', label: 'UZNANA DOSKONAŁOŚĆ', rtl: false },
  { code: 'uk', label: 'ВИЗНАНА ДОСКОНАЛІСТЬ', rtl: false },
  { code: 'bg', label: 'УТВЪРДЕНО СЪВЪРШЕНСТВО', rtl: false },
  { code: 'sr', label: 'УСТАНОВЉЕНА ИЗВРСНОСТ', rtl: false },
  { code: 'ar', label: 'التميز الراسخ', rtl: true },
  { code: 'he', label: 'מצוינות מבוססת', rtl: true },
  { code: 'zh', label: '卓越典范', rtl: false },
  { code: 'hi', label: 'स्थापित उत्कृष्टता', rtl: false },
  { code: 'ne', label: 'स्थापित उत्कृष्टता', rtl: false },
  { code: 'si', label: 'ස්ථාපිත විශිෂ්ටත්වය', rtl: false },
  { code: 'tl', label: 'KILALANG KAHUSAYAN', rtl: false },
  { code: 'vi', label: 'SỰ XUẤT SẮC BỀN VỮNG', rtl: false },
];

const AUTO_DISMISS_MS = 2800;

const LoadingScreen = ({ companyLogo, onDone, alwaysShow }) => {
  const [visible, setVisible] = useState(() => {
    if (alwaysShow) return true;
    try {
      if (new URLSearchParams(window.location.search).has('nosplash')) return false;
    } catch {}
    try {
      return !sessionStorage.getItem('jhouse_splash_shown');
    } catch {
      return true;
    }
  });

  const dismiss = () => {
    setVisible(false);
    try { sessionStorage.setItem('jhouse_splash_shown', '1'); } catch {}
    if (onDone) onDone();
  };

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // No rotation needed — all languages displayed simultaneously below the headline.

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          onClick={dismiss}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0a1628] cursor-pointer select-none"
          data-testid="loading-screen"
          role="button"
          aria-label="Skip intro"
        >
          {/* Radial gold halo */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, rgba(201,168,76,0.18) 0%, transparent 55%)',
            }}
          />

          {/* Animated logo */}
          <motion.img
            src={companyLogo}
            alt="J.House of Secretary"
            className="h-20 md:h-28 w-auto relative z-10"
            initial={{ scale: 0.7, opacity: 0, filter: 'blur(8px)' }}
            animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />

          {/* Greek headline + gold underline */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-6 relative z-10 flex flex-col items-center px-4"
          >
            <p className="text-sm md:text-base uppercase tracking-[0.4em] font-semibold text-[#c9a84c]">
              {TAGLINES[0].label}
            </p>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.8, duration: 0.8, ease: 'easeOut' }}
              className="mt-3 h-px w-52 md:w-80 origin-center"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(201,168,76,0.95), transparent)',
              }}
            />

            {/* All languages in a 3–4 column grid — luxury hotel sign style */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-2 max-w-2xl">
              {TAGLINES.slice(1).map((tagline, i) => (
                <motion.p
                  key={tagline.code}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 + i * 0.05, duration: 0.4, ease: 'easeOut' }}
                  dir={tagline.rtl ? 'rtl' : 'ltr'}
                  className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-[#c9a84c]/70 font-medium text-center"
                >
                  {tagline.label}
                </motion.p>
              ))}
            </div>
          </motion.div>

          {/* Skip hint at bottom */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.3, duration: 0.4 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#c9a84c]/60">
              Πατήστε για είσοδο
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] animate-pulse [animation-delay:200ms]" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
